import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { TreeViewContext } from '../TreeViewContext'
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DeleteIcon,
  WarningTwoIcon,
} from '@chakra-ui/icons'

import { BiPencil } from 'react-icons/bi'
import { renderIcon } from '../TreeView.utils'

const TreeItemAccordion = ({ data, children, disableClick }) => {
  const {
    onItemClick,
    onEditDrilldown,
    selectedNodeId,
    onDelete,
    readOnly,
    lastAddedNodeId,
    onOpenNodePopover,
  } = useContext(TreeViewContext)

  useEffect(() => {
    React.Children.map(children, child => {
      if (child.props.droppableId === lastAddedNodeId) onOpenNodePopover()
    })
  }, [children, lastAddedNodeId, onOpenNodePopover])

  const handleItemClick = useCallback(() => {
    if (!disableClick) {
      onItemClick(data)
    }
  }, [data, disableClick, onItemClick])

  const handleDelete = useCallback(
    e => {
      if (e) {
        e.stopPropagation()
      }

      onDelete(data)
    },
    [data, onDelete],
  )

  const handleEdit = e => {
    if (e) {
      e.stopPropagation()
    }

    onEditDrilldown(data)
  }
  /**
   * Check if the node is a chart OR a drill-down (which are charts)
   * @type {boolean|*}
   */
  const isChart =
    ['drilldown', 'chart'].includes(data.type) ||
    data.type?.startsWith('drilldown')

  const hasMissingLinks = useMemo(
    () =>
      data.children.some(
        item =>
          !Object.values(data.drillDownInfo?.linkages).find(
            id => id === item.id,
          ),
      ),
    [data.children, data.drillDownInfo?.linkages],
  )

  return (
    <Accordion allowToggle={selectedNodeId === data?.id}>
      <AccordionItem
        border="none"
        p="0">
        {({ isExpanded }) => (
          <>
            <AccordionButton
              as="div"
              borderRadius={3}
              bg={
                selectedNodeId === data.id
                  ? 'brand.lightestOrange'
                  : 'transparent'
              }
              cursor="pointer"
              p="4px 24px"
              onClick={handleItemClick}
              _hover={{
                '.delete-icon': {
                  opacity: 1,
                  transition: 'opacity 0.2s ease-in-out',
                  pointerEvents: 'all',
                },
              }}>
              {isExpanded ? (
                <ChevronDownIcon fontSize="14px" />
              ) : (
                <ChevronRightIcon fontSize="14px" />
              )}
              <Box ml="8px">{isChart && renderIcon(data.icon)}</Box>
              <Text
                fontWeight={
                  ['table', 'section'].includes(data.type) ? 'bold' : 'normal'
                }
                fontSize={isChart ? '13px' : '14px'}
                color="brand.textDarkBlue"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis">
                {data.name}
                {isChart && data.children?.length > 0 && (
                  <EditDrilldownButton
                    data={data}
                    onClick={handleEdit}
                    hasMissingLinks={hasMissingLinks}
                  />
                )}
              </Text>
              {!readOnly && (
                <IconButton
                  variant="transparent"
                  ml="8px"
                  size="xs"
                  aria-label={'delete'}
                  icon={<DeleteIcon color="brand.blue" />}
                  onClick={handleDelete}
                  opacity={0}
                  pointerEvents="none"
                  className="delete-icon"
                />
              )}
            </AccordionButton>
            <AccordionPanel p={0}>{children}</AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  )
}

const EditDrilldownButton = ({ data, onClick, hasMissingLinks }) => {
  return (
    <Button
      variant="linkTransparent"
      size="small"
      ml="8px"
      onClick={onClick}
      title="Edit Drilldown"
      color="brand.darkGray">
      <Box display="flex">
        <Text as="span">{data.children?.length || 0} DD</Text>
        <Box
          ml="4px"
          pt="1px">
          <BiPencil
            as="BiPencil"
            color="brand.blue"
          />
        </Box>
        {hasMissingLinks ? (
          <Tooltip
            label="There are drilldowns without links."
            bgColor="brand.orange"
            color="brand.white"
            fontWeight="bold">
            <WarningTwoIcon
              color="brand.red"
              ml="4px"
              mt="1px"
            />
          </Tooltip>
        ) : (
          <Tooltip
            label="All drilldowns has links."
            bgColor="brand.green"
            color="brand.white"
            fontWeight="bold">
            <CheckIcon
              color="brand.green"
              ml="4px"
              mt="1px"
            />
          </Tooltip>
        )}
      </Box>
    </Button>
  )
}
const TreeItemAccordionMemo = React.memo(TreeItemAccordion)
export { TreeItemAccordionMemo as TreeItemAccordion }
